import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { glass } from '../../styles/glass';

const DEFAULT_PALETTE = [
  '#1e3a5f', '#2563eb', '#16a34a', '#e67e22',
  '#dc2626', '#7c3aed', '#06b6d4', '#f59e0b',
];

interface AssetAllocationChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  height?: number;
  showLegend?: boolean;
}

/**
 * Formats a number as a USD currency string.
 */
const formatCurrency = (value: number): string =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Custom tooltip content for the donut chart showing amount and percentage.
 */
const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: entry } = payload[0];
  const total = entry?.totalValue ?? 0;
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        ...glass({ tier: 'overlay', level: 3 }),
      }}
    >
      <Typography variant="body2" fontWeight={600}>{name}</Typography>
      <Typography variant="body2">{formatCurrency(value)}</Typography>
      <Typography variant="caption" color="text.secondary">{pct}%</Typography>
    </Box>
  );
};

/**
 * Custom legend renderer that shows colored dots and names in a wrap layout.
 */
const renderLegend = (props: any) => {
  const { payload } = props;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 1 }}>
      {payload?.map((entry: any, index: number) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
          <Typography variant="caption">{entry.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * A donut pie chart for asset or spending allocation with a center total.
 *
 * Args:
 *   data (Array): Array of objects with name, value, and optional color.
 *   height (number): Chart height in pixels (default 350).
 *   showLegend (boolean): Whether to show the legend below the chart.
 *
 * Returns:
 *   React.ReactElement: The rendered donut chart.
 */
export const AssetAllocationChart: React.FC<AssetAllocationChartProps> = ({
  data,
  height = 350,
  showLegend = true,
}) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);

  // Attach totalValue to each entry so tooltip can compute percentage
  const enrichedData = data.map((d) => ({ ...d, totalValue: total }));

  const renderCenterLabel = useCallback(
    ({ viewBox }: any) => {
      const { cx, cy } = viewBox;
      return (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
          <tspan x={cx} dy="-0.5em" fontSize="14" fill="#888">
            Total
          </tspan>
          <tspan x={cx} dy="1.4em" fontSize="18" fontWeight="700" fill="#333">
            {formatCurrency(total)}
          </tspan>
        </text>
      );
    },
    [total],
  );

  if (data.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No allocation data available
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={enrichedData}
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="80%"
            dataKey="value"
            nameKey="name"
            // Reason: animated Pie sectors fail to mount under React 18
            // StrictMode (labels render, sectors don't) — same workaround as
            // PositionMix. Per-slice labels are dropped: with 20+ categories
            // they overlap into an unreadable tangle; the legend and tooltip
            // carry the names.
            isAnimationActive={false}
          >
            {enrichedData.map((entry, index) => (
              <Cell
                key={index}
                fill={entry.color || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend content={renderLegend} />}
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};
